import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate, formatINR } from "@/lib/sections";
import { Check, Mail, MapPin } from "lucide-react";

export default function Confirmation() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/bookings/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-24 text-muted2">Loading…</div>;
  if (!data) return (
    <div className="p-24 text-center">
      <div className="font-serif text-4xl">Booking not found.</div>
      <Link to="/events" className="mt-6 inline-block link-hover">Back to events</Link>
    </div>
  );

  const { booking, event } = data;
  const isPaid = booking.status === "paid";

  return (
    <div data-testid="confirmation-page" className="max-w-[900px] mx-auto px-6 md:px-12 py-16 lg:py-24">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 border flex items-center justify-center ${isPaid ? "border-sage text-sage" : "border-soft text-muted2"}`} style={isPaid ? {borderColor: "#4A5D4E", color: "#4A5D4E"} : {}}>
          <Check size={18} />
        </div>
        <div className="overline">{isPaid ? "Booking confirmed" : "Booking " + booking.status}</div>
      </div>

      <h1 className="display text-5xl md:text-6xl mt-6">Thank you, {booking.name.split(' ')[0]}.</h1>
      <p className="mt-6 text-base text-muted2 max-w-xl">
        Your seat for <em className="italic">{event?.title || booking.event_title}</em> is reserved. A confirmation
        email has been sent to <strong className="text-charcoal">{booking.email}</strong>. Please bring this booking ID
        with you.
      </p>

      <div className="mt-12 border border-soft p-8 bg-white">
        <div className="overline">Receipt</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">Booking ID</div>
            <div className="font-mono text-sm mt-1" data-testid="confirmation-booking-id">{booking.id}</div>
          </div>
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">Payment ID</div>
            <div className="font-mono text-sm mt-1">{booking.razorpay_payment_id || "—"}</div>
          </div>
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">Event</div>
            <div className="font-serif text-xl mt-1">{event?.title || booking.event_title}</div>
          </div>
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">When</div>
            <div className="text-sm mt-1">{event ? (event.is_recurring ? event.recurrence_label : formatDate(event.starts_at)) : "—"}</div>
          </div>
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">Seats</div>
            <div className="text-sm mt-1">{booking.quantity}</div>
          </div>
          <div>
            <div className="text-muted2 text-xs uppercase tracking-widest">Amount paid</div>
            <div className="font-serif text-xl mt-1">{booking.amount_inr > 0 ? formatINR(booking.amount_inr) : "Free"}</div>
          </div>
        </div>

        <div className="hairline my-6" />

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted2"><MapPin size={14}/> {event?.location || "Carpe Diem, Majorda, Goa"}</div>
          <div className="flex items-center gap-2 text-muted2"><Mail size={14}/> Confirmation sent to {booking.email}</div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/events" className="btn-ghost" data-testid="confirmation-more-events">More events</Link>
        <Link to="/" className="btn-primary" data-testid="confirmation-home">Back home</Link>
      </div>
    </div>
  );
}
